import React from "react";
import {ListItemText} from "@mui/material";
import {Controller, useFormContext, useWatch} from "react-hook-form";
import {Box, Colors, Container, Text} from "@bounce/shared/ui";
import {MenuItem, Select, TextField} from "../../../ui/components";
import {FieldLabel, FormCard, INPUT_SX} from "./FormCard";
import SegmentedToggle from "./SegmentedToggle";
import {
  CADENCE_UNITS,
  CADENCE_UNIT_LABELS,
  PROMO_WEEKDAYS,
  PROMO_WINDOWS,
  START_MODES,
  START_MODE_LABELS,
  WEEKDAY_LABELS,
  WEEKDAY_SHORT_LABELS,
  WINDOW_LABELS,
} from "./constants";
import {parseNumberInput} from "./helpers";
import {PromoFormValues} from "./schema";

const WINDOW_HINT = "How long the promo runs";
const RELATIVE_WINDOW_NOTE = "Relative windows start when the user enters the promo, not at launch.";
const START_ON_DATE_HELP =
  "Goes live on the date you pick. Accounts enter only on that date, so accounts that start matching later never enter.";
const DATE_INPUT_WIDTH = 220;
const CADENCE_EVERY_WIDTH = 80;
const REPEAT_LABEL = "Frequency";
const REPEAT_SELECT_WIDTH = 220;
const REPEAT_MENU_MAX_WIDTH = 480;
const REPEAT_OPTIONS = {
  once: {
    label: "One time",
    description: "Accounts enter this promo only the first time they match its segments. They cannot enter it again.",
  },
  interval: {
    label: "At fixed intervals",
    description:
      "As long as accounts match the promo's segments, they enter it again each time the interval passes after their last promo window ended.",
  },
} as const;
type RepeatOption = keyof typeof REPEAT_OPTIONS;
const CADENCE_QUESTION = "What is the minimum time that should pass before the account can enter the promo again?";

const startHelp = (startMode: PromoFormValues["start_mode"], weekday: PromoFormValues["start_weekday"]) =>
  startMode === "date"
    ? START_ON_DATE_HELP
    : `Goes live on the next ${WEEKDAY_LABELS[weekday]}. Accounts enter only on ${WEEKDAY_LABELS[weekday]}s.`;

const cadenceHelp = (cadenceEvery: PromoFormValues["cadence_every"], cadenceUnit: PromoFormValues["cadence_unit"]) =>
  `${cadenceEvery ?? "…"} ${
    CADENCE_UNIT_LABELS[cadenceUnit]
  }, counted from the day the account's last promo window ended. Accounts removed from the promo early don't wait.`;

const WindowCard = () => {
  const {control} = useFormContext<PromoFormValues>();
  const promoWindow = useWatch({control, name: "window"});
  const startMode = useWatch({control, name: "start_mode"});
  const startWeekday = useWatch({control, name: "start_weekday"});
  const repeats = useWatch({control, name: "repeats"});
  const cadenceEvery = useWatch({control, name: "cadence_every"});
  const cadenceUnit = useWatch({control, name: "cadence_unit"});
  const isCustomWindow = promoWindow === "custom";

  return (
    <FormCard title="Window" hint={WINDOW_HINT}>
      <Container row fullWidth sx={{gap: 1.5, alignItems: "center", flexWrap: "nowrap"}}>
        <Box sx={{width: DATE_INPUT_WIDTH, flexShrink: 0}}>
          <Controller
            name="window"
            control={control}
            render={({field}) => (
              <Select {...field} fullWidth size="small" sx={INPUT_SX}>
                {PROMO_WINDOWS.map((window) => (
                  <MenuItem key={window} value={window}>
                    {WINDOW_LABELS[window]}
                  </MenuItem>
                ))}
              </Select>
            )}
          />
        </Box>
        <Text size={13} sx={{color: Colors.grey._600}}>
          {RELATIVE_WINDOW_NOTE}
        </Text>
      </Container>

      <Box>
        <Controller
          name="start_mode"
          control={control}
          render={({field}) => (
            <SegmentedToggle
              value={field.value}
              options={START_MODES.map((value) => ({value, label: START_MODE_LABELS[value]}))}
              onChange={field.onChange}
            />
          )}
        />
        <Container row sx={{gap: 2, mt: 1.5, alignItems: "flex-start", flexWrap: "wrap"}}>
          <Box sx={startMode === "date" ? {width: DATE_INPUT_WIDTH} : undefined}>
            <FieldLabel required>Start</FieldLabel>
            {startMode === "date" ? (
              <Controller
                key="starts_at"
                name="starts_at"
                control={control}
                render={({field: {ref, ...field}, fieldState}) => (
                  <TextField
                    {...field}
                    inputRef={ref}
                    type="date"
                    fullWidth
                    size="small"
                    error={!!fieldState.error}
                    sx={INPUT_SX}
                  />
                )}
              />
            ) : (
              <Controller
                key="start_weekday"
                name="start_weekday"
                control={control}
                render={({field}) => (
                  <SegmentedToggle
                    value={field.value}
                    options={PROMO_WEEKDAYS.map((value) => ({value, label: WEEKDAY_SHORT_LABELS[value]}))}
                    onChange={field.onChange}
                  />
                )}
              />
            )}
          </Box>
          <Box sx={{width: DATE_INPUT_WIDTH}}>
            <FieldLabel required={isCustomWindow}>End</FieldLabel>
            {isCustomWindow ? (
              <Controller
                name="ends_at"
                control={control}
                render={({field: {ref, ...field}, fieldState}) => (
                  <TextField
                    {...field}
                    inputRef={ref}
                    type="date"
                    fullWidth
                    size="small"
                    error={!!fieldState.error}
                    sx={INPUT_SX}
                  />
                )}
              />
            ) : (
              <TextField
                fullWidth
                size="small"
                disabled
                value={`Set by duration (${WINDOW_LABELS[promoWindow]})`}
                sx={INPUT_SX}
              />
            )}
          </Box>
        </Container>
        <Text size={12} sx={{color: Colors.grey._600, mt: 0.75}}>
          {startHelp(startMode, startWeekday)}
        </Text>
      </Box>

      {startMode === "weekday" && (
        <Box>
          <FieldLabel>{REPEAT_LABEL}</FieldLabel>
          <Box sx={{width: REPEAT_SELECT_WIDTH}}>
            <Controller
              name="repeats"
              control={control}
              render={({field}) => (
                <Select
                  fullWidth
                  size="small"
                  value={field.value ? "interval" : "once"}
                  onChange={(event) => field.onChange((event.target.value as RepeatOption) === "interval")}
                  renderValue={(value) => REPEAT_OPTIONS[value as RepeatOption].label}
                  MenuProps={{PaperProps: {sx: {maxWidth: REPEAT_MENU_MAX_WIDTH}}}}
                  sx={INPUT_SX}
                >
                  {(Object.keys(REPEAT_OPTIONS) as RepeatOption[]).map((option) => (
                    <MenuItem key={option} value={option} sx={{whiteSpace: "normal"}}>
                      <ListItemText
                        primary={REPEAT_OPTIONS[option].label}
                        secondary={REPEAT_OPTIONS[option].description}
                      />
                    </MenuItem>
                  ))}
                </Select>
              )}
            />
          </Box>
          {repeats && (
            <>
              <Text size={13} sx={{color: Colors.grey._700, mt: 1.5, mb: 0.75}}>
                {CADENCE_QUESTION}
              </Text>
              <Container row fullWidth sx={{gap: 1.5, alignItems: "center", flexWrap: "nowrap"}}>
                <Box sx={{width: CADENCE_EVERY_WIDTH}}>
                  <Controller
                    name="cadence_every"
                    control={control}
                    render={({field: {ref, onChange, value, ...field}, fieldState}) => (
                      <TextField
                        {...field}
                        inputRef={ref}
                        type="number"
                        size="small"
                        value={value ?? ""}
                        onChange={(event) => onChange(parseNumberInput(event.target.value))}
                        error={!!fieldState.error}
                        sx={INPUT_SX}
                      />
                    )}
                  />
                </Box>
                <Controller
                  name="cadence_unit"
                  control={control}
                  render={({field}) => (
                    <Select {...field} size="small" sx={INPUT_SX}>
                      {CADENCE_UNITS.map((unit) => (
                        <MenuItem key={unit} value={unit}>
                          {CADENCE_UNIT_LABELS[unit]}
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
              </Container>
              <Text size={12} sx={{color: Colors.grey._600, mt: 0.75}}>
                {cadenceHelp(cadenceEvery, cadenceUnit)}
              </Text>
            </>
          )}
        </Box>
      )}
    </FormCard>
  );
};

export default WindowCard;
